'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Users, Sparkles } from 'lucide-react';
import type { CohortCtaState } from '@/server/services/course.service';

/**
 * Overseas Enrolment Task 20/AC21 — the catalog CTA for a course in a COHORT
 * program.
 *
 * There is no "enrol now" here and there never will be: a cohort program does
 * not open terms, so the family's only move is to register interest and wait for
 * an admin to form a class — unless a class is already running with room, in
 * which case they can try it or join it outright.
 */
export function CohortCourseCTA({
  courseId,
  courseTitle,
  state,
}: {
  courseId: string;
  courseTitle?: string;
  state: CohortCtaState;
}) {
  const t = useTranslations('public.enrollment.cohortCta');

  if (state === 'unavailable') {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
        {t('unavailable')}
      </div>
    );
  }

  const running = state === 'join_now' || state === 'trial';

  return (
    <div className="rounded-lg border border-[#1e306e]/20 bg-[#1e306e]/5 p-4 space-y-3">
      <div className="flex items-center gap-2 font-semibold text-[#1e306e]">
        {running ? <Sparkles className="h-5 w-5" /> : <Users className="h-5 w-5" />}
        {running ? t('runningTitle') : t('waitlistTitle')}
      </div>
      <p className="text-sm text-[#6b7280]">
        {running ? t('runningBody', { course: courseTitle ?? '' }) : t('waitlistBody')}
      </p>
      <Button
        asChild
        className="min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-white font-bold"
      >
        <Link href={`/enrollment/cohort?courseId=${courseId}`}>
          {running ? t('joinNow') : t('joinWaitlist')}
        </Link>
      </Button>
    </div>
  );
}
